import { useId } from 'react';

import CheckIcon from '@/assets/svg/ui-elements/check-green.svg';
import { DeniedReasonTrigger } from '@/shared/modules/permissions';

import { theme } from '../../theme';
import Dots from '../Dots';
import Tooltip from '../Tooltip';
import Typography from '../Typography';
import VisuallyHidden from '../VisuallyHidden';
import { RoleDotColor } from './data';
import { RoleButton, RoleInfo, StackedRoleInfo } from './UserDropdown.styled';

type Props = {
  name: string;
  isActive: boolean;
  dotColor: RoleDotColor;
  onSelect: () => void;
  disabled?: boolean;
  note?: string;
  tooltipTitle?: string;
};

export const RoleItem = ({
  name,
  isActive,
  dotColor,
  onSelect,
  disabled = false,
  note,
  tooltipTitle,
}: Props) => {
  const descriptionId = useId();
  const describedBy = disabled && tooltipTitle ? descriptionId : undefined;

  const nameLabel = (
    <Typography as="span" variant="body2" noWrap>
      {name}
    </Typography>
  );

  const button = (
    <RoleButton
      onClick={disabled ? undefined : onSelect}
      aria-pressed={isActive}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      aria-describedby={describedBy}
    >
      <Dots color={dotColor} size="lg" />

      {note ? (
        <StackedRoleInfo>
          {nameLabel}
          <Typography as="span" variant="body3" color={theme.colors.text.secondary} noWrap>
            {note}
          </Typography>
        </StackedRoleInfo>
      ) : (
        <RoleInfo>{nameLabel}</RoleInfo>
      )}

      {isActive && <CheckIcon />}
    </RoleButton>
  );

  if (!disabled) return button;

  return (
    <>
      <Tooltip title={tooltipTitle} side="top" disabled={!tooltipTitle}>
        <DeniedReasonTrigger denied style={{ display: 'block', width: '100%' }}>
          {button}
        </DeniedReasonTrigger>
      </Tooltip>
      {describedBy && <VisuallyHidden id={describedBy}>{tooltipTitle}</VisuallyHidden>}
    </>
  );
};
